import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Badge,
  Box,
  Button,
  Empty,
  Input,
  Modal,
  Progress,
  Select,
  Spinner,
  Tab,
  goldAlert,
  goldToast,
} from "gold-ui-kit";
import AppShell from "../components/classic/AppShell";
import ClassicWindow from "../components/classic/ClassicWindow";
import Toolbar, { ToolbarButton } from "../components/classic/Toolbar";
import FitDataGrid from "../components/classic/FitDataGrid";
import {
  fetchQrAttendanceLogs,
  fetchQrTerminals,
  manualOpenAttendance,
  manualCompleteAttendance,
  fetchIncompleteAttendanceLogs,
  clearStatus,
  QrAttendanceMethodLabels,
} from "../store/slices/employees/qr-attendance-logs";
import {
  fetchEmployees,
  createEmployeeLeaveApi,
  deleteEmployeeLeaveApi,
  fetchEmployeeLeavesByBranchApi,
} from "../store/slices/employees";
import { useAuth } from "../context/AuthContext";
import { toLocalDateKey, parseLocalDate } from "../utils/date";
import { CheckCircle2, Plus, RefreshCw, Search, XCircle } from "lucide-react";

const formatDateTime = (isoStr) => {
  if (!isoStr) return "—";
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return isoStr;
    return d.toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return isoStr;
  }
};

const formatTimeOnly = (isoStr) => {
  if (!isoStr) return "—";
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return isoStr;
    return d.toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return isoStr;
  }
};

// Returns time string formatted for time input (HH:mm)
const toTimeValue = (dateObj = new Date()) => {
  const pad = (n) => String(n).padStart(2, "0");
  const hh = pad(dateObj.getHours());
  const min = pad(dateObj.getMinutes());
  return `${hh}:${min}`;
};

// Backend stores local time (DateTime.Now), so no "Z"/offset is sent.
const timeToIsoString = (timeStr, baseDateStr) => {
  const pad = (n) => String(n).padStart(2, "0");
  const baseDate = parseLocalDate(baseDateStr);

  let hh = pad(baseDate.getHours());
  let min = pad(baseDate.getMinutes());

  if (timeStr) {
    const parts = timeStr.split(":");
    if (parts.length >= 2) {
      hh = pad(parseInt(parts[0], 10) || 0);
      min = pad(parseInt(parts[1], 10) || 0);
    }
  }

  return `${toLocalDateKey(baseDate)}T${hh}:${min}:00`;
};

// Leave Type Enum Mapping
const LEAVE_TYPE_MAP = {
  1: { label: "Yıllık İzin", emoji: "🏖️", color: "primary" },
  2: { label: "Ücretsiz İzin", emoji: "💤", color: "warning" },
  3: { label: "İdari İzin", emoji: "🏛️", color: "dark" },
  4: { label: "Diğer", emoji: "📋", color: "default" },
};

const getLeaveTypeInfo = (leaveType) => {
  return LEAVE_TYPE_MAP[leaveType] || LEAVE_TYPE_MAP[4];
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" });
  } catch {
    return dateStr;
  }
};

const PersonelDevamListPage = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const { logs, incompleteLogs, terminals, loading, submitting, error, successMessage } = useSelector(
    (s) => s.qrAttendanceLogs
  );
  const { list: employees } = useSelector((s) => s.employees);

  const [search, setSearch] = useState("");
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  // Manuel Açma Form state
  const [openEmployeeId, setOpenEmployeeId] = useState("");
  const [openTerminalId, setOpenTerminalId] = useState("");
  const [openTime, setOpenTime] = useState(toTimeValue());

  // Manuel Kapatma Form state
  const [completeLog, setCompleteLog] = useState(null);
  const [completeTime, setCompleteTime] = useState(toTimeValue());

  // Kullanıcının BranchId bilgisini güvenli bir şekilde al (branchId veya MetaData.BranchId)
  const userBranchId = useMemo(() => {
    if (user?.branchId) return user.branchId;
    if (user?.MetaData) {
      try {
        const meta = typeof user.MetaData === "string" ? JSON.parse(user.MetaData) : user.MetaData;
        return meta.BranchId || meta.branchId || "";
      } catch {}
    }
    return "";
  }, [user]);

  // SADECE kullanıcının şubesine ait terminalleri filtrele (Diğer şubeler seçilemez)
  const branchTerminals = useMemo(() => {
    if (!Array.isArray(terminals)) return [];
    if (!userBranchId) return terminals;
    return terminals.filter(
      (t) => t.branchId && t.branchId.toLowerCase() === userBranchId.toLowerCase()
    );
  }, [terminals, userBranchId]);

  // Terminal listesi gelince / filtre değişince otomatik seç
  useEffect(() => {
    if (branchTerminals.length > 0) {
      const exists = branchTerminals.some((t) => t.id === openTerminalId);
      if (!exists) {
        setOpenTerminalId(branchTerminals[0].id);
      }
    } else {
      setOpenTerminalId("");
    }
  }, [branchTerminals, openTerminalId]);

  // Selected work date (date picker state - defaults to today)
  const [selectedDate, setSelectedDate] = useState(() => toLocalDateKey());
  const todayKey = selectedDate;

  // Boot sequence state
  const BOOT_STEPS = useMemo(() => [
    { key: "logs", label: "Devam kayıtları (loglar) çekiliyor..." },
    { key: "terminals", label: "Terminal listesi çekiliyor..." },
    { key: "employees", label: "Personel listesi çekiliyor..." },
    { key: "leaves", label: "İzin/yokluk kayıtları çekiliyor..." },
  ], []);

  const [bootStatus, setBootStatus] = useState(() => {
    const s = {};
    BOOT_STEPS.forEach((step) => { s[step.key] = "pending"; }); // pending | loading | done | error
    return s;
  });
  const [bootDone, setBootDone] = useState(false);

  const updateBootStep = (key, status) => {
    setBootStatus((prev) => ({ ...prev, [key]: status }));
  };

  // Initial boot sequence
  useEffect(() => {
    if (bootDone) return; // Sadece sayfa ilk açıldığında çalıştır

    let cancelled = false;
    const runBoot = async () => {
      const targetBranch = userBranchId || user?.branchId;

      // Step 1: Logs
      updateBootStep("logs", "loading");
      try {
        await Promise.all([
          dispatch(fetchQrAttendanceLogs(todayKey)).unwrap(),
          targetBranch ? dispatch(fetchIncompleteAttendanceLogs({ branchId: targetBranch })).unwrap() : Promise.resolve(),
        ]);
        if (!cancelled) updateBootStep("logs", "done");
      } catch {
        if (!cancelled) updateBootStep("logs", "error");
      }

      // Step 2: Terminals
      if (!cancelled) updateBootStep("terminals", "loading");
      try {
        await dispatch(fetchQrTerminals()).unwrap();
        if (!cancelled) updateBootStep("terminals", "done");
      } catch {
        if (!cancelled) updateBootStep("terminals", "error");
      }

      // Step 3: Employees
      if (!cancelled) updateBootStep("employees", "loading");
      try {
        if (targetBranch) {
          const periodDate = parseLocalDate(todayKey);
          await dispatch(
            fetchEmployees({
              branchId: targetBranch,
              year: periodDate.getFullYear(),
              month: periodDate.getMonth() + 1,
            })
          ).unwrap();
        }
        if (!cancelled) updateBootStep("employees", "done");
      } catch {
        if (!cancelled) updateBootStep("employees", "error");
      }

      // Step 4: Leaves
      if (!cancelled) updateBootStep("leaves", "loading");
      try {
        if (targetBranch) {
          const leavesData = await fetchEmployeeLeavesByBranchApi(targetBranch, todayKey, todayKey);
          if (!cancelled && Array.isArray(leavesData)) {
            const fetchedMap = {};
            leavesData.forEach((item) => {
              const empId = String(item.employeeId || item.EmployeeId || "").trim().toLowerCase();
              if (empId) {
                fetchedMap[empId] = {
                  id: item.id || item.EmployeeLeaveId,
                  reason: item.reason || item.Reason || "Yok",
                  workDate: item.workDate,
                  startDate: item.startDate,
                  endDate: item.endDate,
                  leaveType: item.leaveType || item.LeaveType || 4,
                  fromServer: true,
                };
              }
            });
            setOnLeaveMap(fetchedMap);
            try {
              localStorage.setItem(`on_leave_map_${todayKey}`, JSON.stringify(fetchedMap));
            } catch {}
          }
        }
        if (!cancelled) updateBootStep("leaves", "done");
      } catch {
        if (!cancelled) updateBootStep("leaves", "error");
      }

      if (!cancelled) setBootDone(true);
    };

    runBoot();
    return () => { cancelled = true; };
  }, [dispatch, userBranchId, user?.branchId, todayKey, BOOT_STEPS]);

  // Toast notifications for error and successMessage
  useEffect(() => {
    if (error) {
      goldToast.error(error, { duration: 4000 });
      dispatch(clearStatus());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (successMessage) {
      goldToast.success(successMessage, { duration: 3000 });
      dispatch(clearStatus());
    }
  }, [successMessage, dispatch]);

  // Handle Manual Open Submit
  const handleOpenSubmit = async (e) => {
    e.preventDefault();
    if (!openEmployeeId || !openTerminalId) return;

    const isoCheckIn = timeToIsoString(openTime, selectedDate);
    const res = await dispatch(
      manualOpenAttendance({
        employeeId: openEmployeeId,
        terminalId: openTerminalId,
        checkInTime: isoCheckIn,
      })
    );

    if (manualOpenAttendance.fulfilled.match(res)) {
      setShowOpenModal(false);
      setOpenEmployeeId("");
      setOpenTime(toTimeValue());
      // Re-sync attendance logs and employees list so missing tab updates instantly
      handleSync();
    }
  };

  // Handle Manual Complete Submit
  const handleCompleteSubmit = async (e) => {
    e.preventDefault();
    if (!completeLog) return;

    const baseDate = completeLog.checkInTime || selectedDate;
    const isoCheckOut = timeToIsoString(completeTime, baseDate);
    const res = await dispatch(
      manualCompleteAttendance({
        id: completeLog.id || completeLog.Id,
        checkOutTime: isoCheckOut,
      })
    );

    if (manualCompleteAttendance.fulfilled.match(res)) {
      setShowCompleteModal(false);
      setCompleteLog(null);
      setCompleteTime(toTimeValue());
      // Re-sync attendance logs and employees list
      handleSync();
    }
  };

  // Quick Action Handle
  const handleQuickComplete = async (log, timeStr) => {
    if (!log || (!log.id && !log.Id)) return;
    
    // Always use the checkInTime for the checkout base date
    const isoCheckOut = timeToIsoString(timeStr, log.checkInTime);
    const res = await dispatch(
      manualCompleteAttendance({
        id: log.id || log.Id,
        checkOutTime: isoCheckOut,
      })
    );

    if (manualCompleteAttendance.fulfilled.match(res)) {
      handleSync();
    }
  };

  // Open complete modal for a specific log
  const openCompleteDialog = (log) => {
    setCompleteLog(log);
    setCompleteTime(toTimeValue());
    setShowCompleteModal(true);
  };

  // Filtered logs list
  const filteredLogs = useMemo(() => {
    if (!Array.isArray(logs)) return [];
    return logs.filter((log) => {
      const fullName = `${log.employeeName || ""} ${log.employeeSurname || ""}`.toLowerCase();
      return fullName.includes(search.toLowerCase());
    });
  }, [logs, search]);

  const methodBadge = (methodId) => {
    const label = QrAttendanceMethodLabels[methodId] || "Bilinmiyor";
    const meta = { 1: ["primary", "📱"], 2: ["dark", "💳"], 3: ["warning", "✍️"] }[methodId];
    if (!meta) return <span className="text-xs text-slate-400">—</span>;
    return (
      <Badge size="sm" variant="soft" color={meta[0]}>
        {meta[1]} {label}
      </Badge>
    );
  };

  const [activeTab, setActiveTab] = useState("all"); // "all" | "missing"
  const [syncing, setSyncing] = useState(false);

  // Leave State (persisted per day in localStorage with Reasons)
  const [onLeaveMap, setOnLeaveMap] = useState(() => {
    try {
      const saved = localStorage.getItem(`on_leave_map_${todayKey}`);
      if (saved) return JSON.parse(saved);

      const oldSaved = localStorage.getItem(`on_leave_employees_${todayKey}`);
      if (oldSaved) {
        const arr = JSON.parse(oldSaved);
        const map = {};
        arr.forEach((id) => {
          map[String(id).toLowerCase()] = { reason: "Mazeret İzni" };
        });
        return map;
      }
      return {};
    } catch {
      return {};
    }
  });

  const toggleEmployeeLeave = (emp) => {
    if (!emp) return;
    const empId = emp.id || emp.EmployeeId;
    if (!empId) return;

    const empName = `${emp.name || emp.firstName || ""} ${emp.surname || emp.lastName || ""}`.trim();
    const norm = String(empId).toLowerCase();
    const leaveData = onLeaveMap[norm];
    const isCurrentlyLeave = !!leaveData;

    if (!isCurrentlyLeave) {
      const CONFIRM_DELAY = 5;
      let timerInterval;

      goldAlert
        .fire({
          title: "Bugün Yok İşaretlensin mi?",
          html: (
            <>
              <b>{empName}</b> isimli personeli bugün <b className="text-blue-600">Yok</b> olarak kayıt etmek
              üzeresiniz.
            </>
          ),
          input: "text",
          inputLabel: "Yokluk Nedeni / Açıklama:",
          inputPlaceholder: "Örn: İzinli, Raporlu, Sevk, Mazeretsiz...",
          inputValue: "İzinli",
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "primary",
          cancelButtonColor: "default",
          confirmButtonText: `Evet, Yok İşaretle (${CONFIRM_DELAY}s)`,
          cancelButtonText: "Vazgeç",
          buttonsProps: { confirm: { disabled: true } },
          inputValidator: (value) =>
            !value || !String(value).trim() ? "Lütfen yokluk nedenini belirtiniz!" : null,
          didOpen: () => {
            let countdown = CONFIRM_DELAY;
            timerInterval = setInterval(() => {
              countdown -= 1;
              if (countdown > 0) {
                goldAlert.update({ confirmButtonText: `Evet, Yok İşaretle (${countdown}s)` });
              } else {
                clearInterval(timerInterval);
                goldAlert.update({
                  confirmButtonText: "Evet, Yok İşaretle",
                  buttonsProps: { confirm: { disabled: false } },
                });
              }
            }, 1000);
          },
          willClose: () => clearInterval(timerInterval),
        })
        .then(async (result) => {
          if (!result.isConfirmed) return;
          const reason = String(result.value || "İzinli").trim();
          let serverLeaveId = null;

          try {
            const apiRes = await createEmployeeLeaveApi(empId, reason, todayKey);
            serverLeaveId = apiRes?.id || apiRes?.data?.id || apiRes?.EmployeeLeaveId || null;
          } catch (apiErr) {
            console.error("EmployeeLeave POST hatası:", apiErr);
            const serverMsg =
              apiErr?.response?.data?.ExceptionMessage ||
              apiErr?.response?.data?.message ||
              "Sunucu hatası nedeniyle yokluk kaydı oluşturulamadı.";
            goldAlert.fire({
              title: "Kayıt Başarısız!",
              html: (
                <>
                  <b>{empName}</b> için yokluk kaydı oluşturulamadı.
                  <br />
                  <span className="text-red-600">{serverMsg}</span>
                </>
              ),
              icon: "error",
              confirmButtonText: "Tamam",
            });
            return;
          }

          setOnLeaveMap((prev) => {
            const updated = {
              ...prev,
              [norm]: {
                id: serverLeaveId,
                reason,
                time: new Date().toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }),
              },
            };
            try {
              localStorage.setItem(`on_leave_map_${todayKey}`, JSON.stringify(updated));
            } catch {}
            return updated;
          });

          goldAlert.fire({
            title: "Bugün Yok Kaydedildi!",
            html: (
              <>
                <b>{empName}</b> bugün <b>{reason}</b> nedeniyle Yok kaydedildi.
              </>
            ),
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
        });
    } else {
      goldAlert
        .fire({
          title: "Yokluk Kaydı Kaldırılsın mı?",
          html: (
            <>
              <b>{empName}</b> personeline ait <b>"{leaveData.reason || "Yok"}"</b> kaydını iptal etmek istediğinize
              emin misiniz?
            </>
          ),
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "danger",
          cancelButtonColor: "default",
          confirmButtonText: "Evet, İptal Et",
          cancelButtonText: "Vazgeç",
        })
        .then(async (result) => {
          if (!result.isConfirmed) return;
          if (leaveData?.id) {
            try {
              await deleteEmployeeLeaveApi(leaveData.id);
            } catch (apiErr) {
              console.error("EmployeeLeave DELETE hatası:", apiErr);
              const serverMsg =
                apiErr?.response?.data?.ExceptionMessage ||
                apiErr?.response?.data?.message ||
                "Sunucu hatası nedeniyle yokluk kaydı silinemedi.";
              goldAlert.fire({
                title: "Silme Başarısız!",
                html: (
                  <>
                    <b>{empName}</b> için yokluk kaydı silinemedi.
                    <br />
                    <span className="text-red-600">{serverMsg}</span>
                  </>
                ),
                icon: "error",
                confirmButtonText: "Tamam",
              });
              return;
            }
          }

          setOnLeaveMap((prev) => {
            const updated = { ...prev };
            delete updated[norm];
            try {
              localStorage.setItem(`on_leave_map_${todayKey}`, JSON.stringify(updated));
            } catch {}
            return updated;
          });

          goldAlert.fire({
            title: "Yokluk Kaydı Silindi",
            text: `${empName} için kaydolmuş yokluk bilgisi silindi.`,
            icon: "info",
            timer: 1800,
            showConfirmButton: false,
          });
        });
    }
  };

  // Sync handler: Refetches logs, terminals, employees and leaves for user's branch
  const handleSync = async () => {
    setSyncing(true);
    try {
      const targetBranch = userBranchId || user?.branchId;
      const periodDate = parseLocalDate(todayKey);
      const [,,, leavesData] = await Promise.all([
        dispatch(fetchQrAttendanceLogs(todayKey)),
        targetBranch ? dispatch(fetchIncompleteAttendanceLogs({ branchId: targetBranch })) : Promise.resolve(),
        dispatch(fetchQrTerminals()),
        // null (not []) on failure so the existing leave map is kept instead of being wiped
        targetBranch
          ? fetchEmployeeLeavesByBranchApi(targetBranch, todayKey, todayKey).catch((err) => {
              console.error("İzin kayıtları alınamadı:", err);
              return null;
            })
          : Promise.resolve(null),
        targetBranch
          ? dispatch(
              fetchEmployees({
                branchId: targetBranch,
                year: periodDate.getFullYear(),
                month: periodDate.getMonth() + 1,
              })
            )
          : Promise.resolve(),
      ]);

      if (Array.isArray(leavesData)) {
        const fetchedMap = {};
        leavesData.forEach((item) => {
          const empId = String(item.employeeId || item.EmployeeId || "").trim().toLowerCase();
          if (empId) {
            fetchedMap[empId] = {
              id: item.id || item.EmployeeLeaveId,
              reason: item.reason || item.Reason || "Yok",
              workDate: item.workDate,
              startDate: item.startDate,
              endDate: item.endDate,
              leaveType: item.leaveType || item.LeaveType || 4,
              fromServer: true,
            };
          }
        });
        setOnLeaveMap(fetchedMap);
        try {
          localStorage.setItem(`on_leave_map_${todayKey}`, JSON.stringify(fetchedMap));
        } catch {}
      } else if (targetBranch) {
        goldToast.warning("İzin kayıtları güncellenemedi, gösterilen izin bilgileri eski olabilir.", {
          duration: 4000,
        });
      }
    } catch (err) {
      console.error("Senkronizasyon hatası:", err);
    } finally {
      setSyncing(false);
    }
  };

  // Auto-fetch when selectedDate changes (only after initial boot)
  useEffect(() => {
    if (bootDone) {
      handleSync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Compute missing employees: compares logs vs branch employees by both ID & Full Name
  // Any employee who has checked-in (appeared in logs) is automatically removed from missingEmployees list!
  const missingEmployees = useMemo(() => {
    if (!Array.isArray(employees)) return [];
    if (!Array.isArray(logs)) return employees;

    // 1. Collect set of checked-in Employee IDs (lowercase string)
    const checkedInIds = new Set(
      logs
        .map((l) => String(l.employeeId || l.EmployeeId || "").trim().toLowerCase())
        .filter(Boolean)
    );

    // 2. Collect set of checked-in Employee Full Names (lowercase string: "name surname")
    const checkedInNames = new Set(
      logs
        .map((l) => {
          const name = String(l.employeeName || "").trim().toLowerCase();
          const surname = String(l.employeeSurname || "").trim().toLowerCase();
          return `${name} ${surname}`.trim();
        })
        .filter((n) => n.length > 1)
    );

    // 3. Filter employees: Remove any employee who matches by ID OR by Full Name in logs
    return employees.filter((emp) => {
      // Exclude resigned / former employees
      if (emp.exitDate || emp.ExitDate || emp.resignationDate || emp.ResignationDate) return false;

      const empId = String(emp.id || emp.EmployeeId || "").trim().toLowerCase();
      const name = String(emp.name || emp.firstName || "").trim().toLowerCase();
      const surname = String(emp.surname || emp.lastName || "").trim().toLowerCase();
      const fullName = `${name} ${surname}`.trim();

      const hasCheckedInById = empId && checkedInIds.has(empId);
      const hasCheckedInByName = fullName && checkedInNames.has(fullName);

      // Keep ONLY employees who have NOT checked in
      return !hasCheckedInById && !hasCheckedInByName;
    });
  }, [employees, logs]);

  const filteredMissingEmployees = useMemo(() => {
    if (!search) return missingEmployees;
    const q = search.toLowerCase();
    return missingEmployees.filter((emp) => {
      const name = `${emp.name || emp.firstName || ""} ${emp.surname || emp.lastName || ""}`.toLowerCase();
      return name.includes(q);
    });
  }, [missingEmployees, search]);

  const filteredIncompleteLogs = useMemo(() => {
    if (!Array.isArray(incompleteLogs)) return [];
    if (!search) return incompleteLogs;
    const q = search.toLowerCase();
    return incompleteLogs.filter((log) => {
      const name = `${log.employeeName || ""} ${log.employeeSurname || ""}`.toLowerCase();
      return name.includes(q);
    });
  }, [incompleteLogs, search]);

  // Boot progress percentage
  const bootProgress = useMemo(() => {
    const total = BOOT_STEPS.length;
    const done = BOOT_STEPS.filter((s) => bootStatus[s.key] === "done" || bootStatus[s.key] === "error").length;
    return Math.round((done / total) * 100);
  }, [bootStatus, BOOT_STEPS]);

  if (!bootDone) {
    return (
      <AppShell pageId="devam">
        <div className="flex min-h-[60vh] items-center justify-center">
          <Box className="flex w-full max-w-md flex-col gap-5 !overflow-visible !rounded-2xl !border-slate-200 !p-6 !shadow-xl sm:!p-8">
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <Spinner size={28} color="primary" />
              <div>
                <h2 className="text-sm font-bold text-slate-900">Sayfa Hazırlanıyor</h2>
                <p className="text-[11px] text-slate-500">Veriler sunucudan çekiliyor, lütfen bekleyin...</p>
              </div>
            </div>

            <div className="flex flex-col gap-2.5">
              {BOOT_STEPS.map((step) => {
                const status = bootStatus[step.key];
                return (
                  <div key={step.key} className="flex items-center gap-3 text-xs">
                    <div className="flex h-5 w-5 shrink-0 items-center justify-center">
                      {status === "done" ? (
                        <CheckCircle2 size={16} className="text-emerald-500" />
                      ) : status === "error" ? (
                        <XCircle size={16} className="text-rose-500" />
                      ) : status === "loading" ? (
                        <Spinner size={14} thickness={2} color="primary" />
                      ) : (
                        <span className="h-2 w-2 rounded-full bg-slate-300" />
                      )}
                    </div>
                    <span
                      className={`font-medium ${
                        status === "done"
                          ? "text-emerald-700"
                          : status === "error"
                          ? "text-rose-600"
                          : status === "loading"
                          ? "text-blue-700"
                          : "text-slate-400"
                      }`}
                    >
                      {status === "done"
                        ? step.label.replace("çekiliyor...", "çekildi ✓")
                        : status === "error"
                        ? step.label.replace("çekiliyor...", "— hata oluştu!")
                        : step.label}
                    </span>
                  </div>
                );
              })}
            </div>

            <Progress value={bootProgress} color="primary" size="sm" showInfo striped />
          </Box>
        </div>
      </AppShell>
    );
  }

  const openManualEntry = (empId = "") => {
    setOpenEmployeeId(empId);
    setOpenTime(toTimeValue());
    setShowOpenModal(true);
  };

  const employeeFullName = (emp) => `${emp.name || emp.firstName || ""} ${emp.surname || emp.lastName || ""}`.trim();

  const leaveStatusBadge = (leaveData) => {
    if (!leaveData) {
      return (
        <Badge size="sm" radius="full" variant="soft" color="danger">
          Giriş Kaydı Yok
        </Badge>
      );
    }
    if (leaveData.fromServer) {
      const info = getLeaveTypeInfo(leaveData.leaveType);
      // Single line so it fits the grid's fixed row height; the reason is in the tooltip.
      return (
        <span className="flex items-center gap-1.5" title={leaveData.reason || ""}>
          <Badge size="sm" radius="full" variant="soft" color={info.color}>
            {info.emoji} {info.label}
          </Badge>
          {leaveData.startDate && leaveData.endDate && (
            <span className="text-[10px] font-medium text-slate-500">
              {formatDateShort(leaveData.startDate)} — {formatDateShort(leaveData.endDate)}
            </span>
          )}
        </span>
      );
    }
    return (
      <Badge size="sm" radius="full" variant="soft" color="default" title={`Yokluk Nedeni: ${leaveData.reason || "Yok"}`}>
        🚫 Bugün Yok ({leaveData.reason || "Yok"})
      </Badge>
    );
  };

  const missingActions = (emp, leaveData, fullWidth = false) => {
    if (leaveData?.fromServer) {
      return <span className="select-none text-[11px] italic text-slate-400">İzin kaydı mevcut — işlem yapılamaz</span>;
    }
    const empId = emp.id || emp.EmployeeId;
    return (
      <div className={`flex items-center gap-2 ${fullWidth ? "w-full" : "justify-center"}`}>
        {!leaveData && (
          <Button size="xs" className={fullWidth ? "!w-full" : ""} onClick={() => openManualEntry(empId)} title="Bu personel için manuel giriş aç">
            ✍️ Manuel Giriş Aç
          </Button>
        )}
        <Button
          size="xs"
          variant="filled"
          color={leaveData ? "warning" : "default"}
          className={fullWidth ? "!w-full" : ""}
          onClick={() => toggleEmployeeLeave(emp)}
          title={leaveData ? "Yokluk kaydını kaldır" : "Personeli bugün yok olarak işaretle"}
        >
          {leaveData ? "❌ Kaydı İptal Et" : "🚫 Bugün Yok"}
        </Button>
      </div>
    );
  };

  const incompleteActions = (log, fullWidth = false) =>
    log.id || log.Id ? (
      <div className={`flex flex-wrap items-center gap-2 ${fullWidth ? "" : "justify-center"}`}>
        <Button size="xs" variant="filled" color="warning" disabled={submitting} onClick={() => handleQuickComplete(log, "18:00")}>
          18:00'da
        </Button>
        <Button size="xs" variant="filled" color="warning" disabled={submitting} onClick={() => handleQuickComplete(log, "21:00")}>
          21:00'da
        </Button>
        <Button size="xs" disabled={submitting} onClick={() => openCompleteDialog(log)}>
          ✍️ Farklı Saat
        </Button>
      </div>
    ) : (
      <span className="text-xs italic text-slate-400" title="Kayıt kimliği (id) eksik olduğu için işlem yapılamıyor.">
        Kimlik Yok
      </span>
    );

  const logRows = filteredLogs.map((log, idx) => ({ ...log, _key: String(log.id ?? log.Id ?? idx) }));
  const missingRows = filteredMissingEmployees.map((emp, idx) => ({
    ...emp,
    _key: String(emp.id || emp.EmployeeId || idx),
    _no: idx + 1,
  }));
  const incompleteRows = filteredIncompleteLogs.map((log, idx) => ({
    ...log,
    _key: String(log.id || log.Id || log.employeeId || idx),
    _no: idx + 1,
  }));

  const logColumns = [
    {
      key: "employee",
      dataIndex: "employeeName",
      title: "Personel", flex: 1, minWidth: 180,
      render: (_, log) => (
        <span className="font-semibold text-slate-900">
          {log.employeeName} {log.employeeSurname}
        </span>
      ),
    },
    {
      key: "checkIn",
      dataIndex: "checkInTime",
      title: "Giriş Saati", width: 150,
      render: (_, log) => (
        <div>
          <span className="font-semibold text-slate-800">{formatTimeOnly(log.checkInTime)}</span>
          <span className="block text-[10px] text-slate-400">{formatDateTime(log.checkInTime)}</span>
        </div>
      ),
    },
    { key: "openedMethod", dataIndex: "openedMethod", title: "Giriş Yöntemi", width: 150, render: (_, log) => methodBadge(log.openedMethod) },
    {
      key: "checkOut",
      dataIndex: "checkOutTime",
      title: "Çıkış Saati", width: 150,
      render: (_, log) =>
        log.checkOutTime ? (
          <div>
            <span className="font-semibold text-slate-800">{formatTimeOnly(log.checkOutTime)}</span>
            <span className="block text-[10px] text-slate-400">{formatDateTime(log.checkOutTime)}</span>
          </div>
        ) : (
          <span className="italic text-slate-400">—</span>
        ),
    },
    {
      key: "closedMethod",
      dataIndex: "closedMethod",
      title: "Çıkış Yöntemi", width: 150,
      render: (_, log) => (log.closedMethod ? methodBadge(log.closedMethod) : <span className="italic text-slate-400">—</span>),
    },
    {
      key: "state",
      dataIndex: "checkOutTime",
      title: "Durum", width: 140,
      align: "center",
      render: (_, log) =>
        !log.checkOutTime ? (
          <Badge size="sm" radius="full" variant="soft" color="success">
            ● Devam Ediyor
          </Badge>
        ) : (
          <Badge size="sm" radius="full" variant="soft" color="default">
            ✓ Tamamlandı
          </Badge>
        ),
    },
    {
      key: "action",
      dataIndex: "id",
      title: "İşlem", width: 160,
      align: "center",
      render: (_, log) =>
        !log.checkOutTime ? (
          <Button size="xs" variant="filled" color="danger" onClick={() => openCompleteDialog(log)} title="Manuel olarak mesai bitir / çıkış kaydı tamamla">
            Manuel Çıkış Yap
          </Button>
        ) : (
          <span className="select-none text-xs text-slate-400">Tamamlandı</span>
        ),
    },
  ];

  const missingColumns = [
    {
      key: "employee",
      dataIndex: "name",
      title: "Personel Adı Soyadı", flex: 1, minWidth: 200,
      render: (_, emp) => <span className="font-semibold text-slate-900">{employeeFullName(emp)}</span>,
    },
    {
      key: "state",
      dataIndex: "_key",
      title: "Durum", width: 300,
      align: "center",
      render: (_, emp) => leaveStatusBadge(onLeaveMap[String(emp.id || emp.EmployeeId || "").toLowerCase()]),
    },
    {
      key: "actions",
      dataIndex: "_key",
      title: "Hızlı İşlem", width: 300,
      align: "center",
      render: (_, emp) => missingActions(emp, onLeaveMap[String(emp.id || emp.EmployeeId || "").toLowerCase()]),
    },
  ];

  const incompleteColumns = [
    {
      key: "employee",
      dataIndex: "employeeName",
      title: "Personel Adı Soyadı", flex: 1, minWidth: 200,
      render: (_, log) => (
        <span className="font-semibold text-slate-900">
          {log.employeeName} {log.employeeSurname}
        </span>
      ),
    },
    {
      key: "checkIn",
      dataIndex: "checkInTime",
      title: "Giriş Saati", width: 180,
      render: (_, log) => <span className="font-semibold text-slate-700">{formatDateTime(log.checkInTime)}</span>,
    },
    { key: "actions", dataIndex: "_key", title: "İşlem", width: 320, align: "center", render: (_, log) => incompleteActions(log) },
  ];

  const tableLoading = loading || syncing;

  const refreshBar = (tone, text, label) => (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <Alert size="sm" variant="filled" color={tone} description={text} className="flex-1" />
      <Button size="sm" variant="outline" color={tone === "danger" ? "danger" : "warning"} onClick={handleSync} loading={syncing} disabled={loading}>
        <span className="flex items-center gap-1.5">
          <RefreshCw size={14} /> {label}
        </span>
      </Button>
    </div>
  );

  const allTab = (
    <div className="flex flex-col gap-4">
      <div className="-mx-3 hidden sm:-mx-4 md:block">
        <FitDataGrid
          uniqueKey="_key"
          columns={logColumns}
          dataSource={logRows}
          loading={tableLoading}
          rowHeight={44}
          emptyFeedback={<Empty size="sm" title="Devam kaydı bulunamadı" description="Seçilen kriterlere uygun devam kaydı yok." />}
        />
      </div>

      <div className="flex flex-1 flex-col gap-2.5 md:hidden">
        {logRows.length === 0 ? (
          <Empty size="sm" title={tableLoading ? "Yükleniyor..." : "Devam kaydı bulunamadı"} description="Seçilen kriterlere uygun devam kaydı yok." />
        ) : (
          logRows.map((log) => {
            const isActive = !log.checkOutTime;
            return (
              <Box key={log._key} className="flex flex-col gap-2 !overflow-visible !rounded-xl !border-slate-200 !p-3 !shadow-sm">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <span className="text-sm font-bold text-slate-900">
                    {log.employeeName} {log.employeeSurname}
                  </span>
                  <Badge size="sm" radius="full" variant="soft" color={isActive ? "success" : "default"}>
                    {isActive ? "● Devam Ediyor" : "✓ Tamamlandı"}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 rounded-lg border border-slate-100 bg-slate-50 p-2.5 text-xs">
                  <div>
                    <span className="block text-[10px] font-bold uppercase text-slate-400">Giriş</span>
                    <span className="text-sm font-extrabold text-slate-800">{formatTimeOnly(log.checkInTime)}</span>
                    <div className="mt-1">{methodBadge(log.openedMethod)}</div>
                  </div>
                  <div>
                    <span className="block text-[10px] font-bold uppercase text-slate-400">Çıkış</span>
                    {log.checkOutTime ? (
                      <>
                        <span className="text-sm font-extrabold text-slate-800">{formatTimeOnly(log.checkOutTime)}</span>
                        {log.closedMethod && <div className="mt-1">{methodBadge(log.closedMethod)}</div>}
                      </>
                    ) : (
                      <span className="mt-1 block text-[11px] italic text-slate-400">Giriş Var, Çıkış Yok</span>
                    )}
                  </div>
                </div>
                {isActive && (
                  <Button size="sm" variant="filled" color="danger" className="!w-full" onClick={() => openCompleteDialog(log)}>
                    Manuel Çıkış Yap
                  </Button>
                )}
              </Box>
            );
          })
        )}
      </div>

    </div>
  );

  const missingTab = (
    <div className="flex flex-col gap-3">
      {refreshBar(
        "danger",
        "Şubenize kayıtlı olup bugün henüz giriş kaydı (QR/Kart) bulunmayan personeller gösteriliyor.",
        "Okutmayanları Yeniden Sorgula"
      )}

      <div className="-mx-3 hidden sm:-mx-4 md:block">
        <FitDataGrid
          uniqueKey="_key"
          columns={missingColumns}
          dataSource={missingRows}
          loading={tableLoading}
          rowHeight={48}
          emptyFeedback={<Empty size="sm" title="🎉 Harika!" description="Şubedeki tüm personeller QR/Kart okutmuş veya giriş yapmış." />}
        />
      </div>

      <div className="flex flex-col gap-2.5 md:hidden">
        {missingRows.length === 0 ? (
          <Empty size="sm" title="🎉 Harika!" description="Şubedeki tüm personeller QR/Kart okutmuş." />
        ) : (
          missingRows.map((emp) => {
            const leaveData = onLeaveMap[String(emp.id || emp.EmployeeId || "").toLowerCase()];
            return (
              <Box key={emp._key} className="flex flex-col gap-2 !overflow-visible !rounded-xl !border-rose-200 !p-3 !shadow-sm">
                <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2">
                  <span className="text-sm font-bold text-slate-900">{employeeFullName(emp)}</span>
                  {leaveStatusBadge(leaveData)}
                </div>
                {missingActions(emp, leaveData, true)}
              </Box>
            );
          })
        )}
      </div>
    </div>
  );

  const incompleteTab = (
    <div className="flex flex-col gap-3">
      {refreshBar("warning", "Şubenizde giriş yapmış ancak çıkış yapmamış tüm açık kayıtlar gösteriliyor.", "Yeniden Sorgula")}

      <div className="-mx-3 hidden sm:-mx-4 md:block">
        <FitDataGrid
          uniqueKey="_key"
          columns={incompleteColumns}
          dataSource={incompleteRows}
          loading={tableLoading}
          rowHeight={44}
          emptyFeedback={<Empty size="sm" title="🎉 Harika!" description="Açık kalan devam kaydı bulunmamaktadır." />}
        />
      </div>

      <div className="flex flex-col gap-2.5 md:hidden">
        {incompleteRows.length === 0 ? (
          <Empty size="sm" title="🎉 Harika!" description="Açık kalan devam kaydı bulunmamaktadır." />
        ) : (
          incompleteRows.map((log) => (
            <Box key={log._key} className="flex flex-col gap-2 !overflow-visible !rounded-xl !border-orange-200 !p-3 !shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <span className="text-sm font-bold text-slate-900">
                  {log.employeeName} {log.employeeSurname}
                </span>
                <Badge size="sm" radius="full" variant="soft" color="warning">
                  #{log._no}
                </Badge>
              </div>
              <div className="text-xs">
                <span className="block text-[10px] font-bold uppercase text-slate-500">Giriş Saati</span>
                <span className="font-bold text-slate-800">{formatDateTime(log.checkInTime)}</span>
              </div>
              {incompleteActions(log, true)}
            </Box>
          ))
        )}
      </div>
    </div>
  );

  const tabTitle = (text, count, color) => (
    <span className="flex items-center gap-2">
      {text}
      <Badge size="xs" radius="full" variant="soft" color={color}>
        {count}
      </Badge>
    </span>
  );

  const employeeOptions = (Array.isArray(employees) ? employees : [])
    .filter((emp) => !emp.exitDate && !emp.ExitDate && !emp.resignationDate && !emp.ResignationDate)
    .map((emp) => ({
      value: emp.id || emp.EmployeeId,
      label: `${emp.name || emp.firstName || emp.EmployeeName || ""} ${emp.surname || emp.lastName || emp.EmployeeSurname || ""}`.trim(),
    }));
  const terminalOptions = branchTerminals.map((t) => ({ value: t.id, label: t.name || "Terminal" }));

  return (
    <AppShell pageId="devam">
      <ClassicWindow title="Personel Devam Listesi (QR / Kart / Yoklama)" width="100%">
        <Toolbar>
          <div className="w-40">
            <Input
              size="sm"
              type="date"
              value={selectedDate}
              max={toLocalDateKey()}
              onChange={(e) => setSelectedDate(e.target.value)}
              title="Çalışma Tarihi Seçin"
            />
          </div>
          <ToolbarButton
            icon={<RefreshCw size={14} className={loading || syncing ? "animate-spin" : ""} />}
            label={syncing ? "Senkronize Ediliyor..." : "Senkronize Et"}
            onClick={handleSync}
            disabled={loading || syncing}
          />
          <ToolbarButton icon={<Plus size={14} />} label="Manuel Giriş Aç" active color="primary" onClick={() => openManualEntry()} />
          <div className="flex-1" />
          <div className="w-full sm:w-64">
            <Input
              size="sm"
              icon={<Search size={14} />}
              placeholder={activeTab === "all" ? "Devam Kaydı Ara..." : "Personel Ara..."}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </Toolbar>

        <div className="p-3 sm:p-4">
          <Tab
            variant="filled"
            className="!w-full [&>div:first-child]:flex-wrap"
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key);
              if (key !== "all") handleSync();
            }}
            items={[
              { key: "all", title: tabTitle("📋 Devam Kayıtları", logs.length, "primary"), content: activeTab === "all" ? allTab : null },
              {
                key: "missing",
                title: tabTitle("⚠️ Okutmayanlar", missingEmployees.length, missingEmployees.length > 0 ? "danger" : "default"),
                content: activeTab === "missing" ? missingTab : null,
              },
              {
                key: "incomplete",
                title: tabTitle("⏳ Açık Kalan Kayıtlar", incompleteLogs?.length || 0, incompleteLogs?.length > 0 ? "warning" : "default"),
                content: activeTab === "incomplete" ? incompleteTab : null,
              },
            ]}
          />
        </div>
      </ClassicWindow>

      <Modal
        isOpen={showOpenModal}
        onClose={() => setShowOpenModal(false)}
        width={460}
        className="max-w-[95vw] !overflow-visible"
        title="Manuel Devam Kaydı Aç (Check-In)"
      >
        <form onSubmit={handleOpenSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-slate-700">Personel</span>
            <Select
              searchable
              searchPlaceholder="Personel ara…"
              placeholder="— Personel Seçiniz —"
              emptyMessage="Personel bulunamadı"
              options={employeeOptions}
              value={openEmployeeId}
              onChange={(e) => setOpenEmployeeId(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-slate-700">Terminal</span>
            {branchTerminals.length === 0 ? (
              <Alert size="sm" variant="filled" color="warning" description="Şubenize ait tanımlı terminal bulunamadı." />
            ) : (
              <>
                <Select
                  options={terminalOptions}
                  value={openTerminalId}
                  disabled={branchTerminals.length === 1}
                  onChange={(e) => setOpenTerminalId(e.target.value)}
                />
                {branchTerminals.length === 1 && (
                  <span className="text-[10px] text-slate-500">* Şubenize ait tanımlı terminal otomatik seçildi.</span>
                )}
              </>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-slate-700">İşe Giriş Saati</span>
            <Input type="time" value={openTime} onChange={(e) => setOpenTime(e.target.value)} required />
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
            <Button type="button" variant="outline" color="default" onClick={() => setShowOpenModal(false)} disabled={submitting}>
              İptal
            </Button>
            <Button type="submit" loading={submitting} disabled={!openEmployeeId || !openTerminalId}>
              Manuel Giriş Oluştur
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showCompleteModal && !!completeLog}
        onClose={() => setShowCompleteModal(false)}
        width={440}
        className="max-w-[95vw]"
        title="Manuel Mesai Çıkışı Tamamla (Check-Out)"
      >
        {completeLog && (
          <form onSubmit={handleCompleteSubmit} className="flex flex-col gap-4">
            <Alert
              size="sm"
              variant="filled"
              color="primary"
              title={`${completeLog.employeeName || ""} ${completeLog.employeeSurname || ""}`.trim() || "Personel"}
              description={`Giriş Saati: ${formatDateTime(completeLog.checkInTime)}`}
            />
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-slate-700">İşten Çıkış Saati</span>
              <Input type="time" value={completeTime} onChange={(e) => setCompleteTime(e.target.value)} required />
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
              <Button type="button" variant="outline" color="default" onClick={() => setShowCompleteModal(false)} disabled={submitting}>
                İptal
              </Button>
              <Button type="submit" loading={submitting}>
                Manuel Çıkışı Kaydet
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </AppShell>
  );
};

export default PersonelDevamListPage;
